import React, { useEffect, useRef, useState } from "react";
import { Typography, Button } from "antd";
import API_SERVICE from "shared/services/api-service";
import { ReactComponent as IconDownload } from "../../assets/images/DownloadQR.svg";
import { ReactComponent as IconBackArrow } from "../../assets/images/BackArrow_Black.svg";
import { ReactComponent as IconPrint } from "../../assets/images/Print.svg";
import { ReactComponent as Icon1Flo } from "../../assets/images/lending_page/1flo.svg";
import { ShareAltOutlined } from "@ant-design/icons";
import { getUser } from "shared/services/Utility";
import { QRCodeCanvas } from "qrcode.react";
import { useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import "./QRCodeGenerator.scss";

const { Text } = Typography;

interface IQRCodeGenerator {
  showQRCode: any;
  setShowQRCode: any;
  setPrintingInstructions: any;
  qRCodeType: any;
  qRCodeUrl: any;
  errorMessage: any;
  showErrorScreen: any;
  setShowErrorScreen: any;
}

const QRCodeGenerator: React.FC<IQRCodeGenerator> = (
  props: IQRCodeGenerator
) => {
  const {
    showQRCode,
    setShowQRCode,
    setPrintingInstructions,
    qRCodeType,
    qRCodeUrl,
    errorMessage,
    showErrorScreen,
    setShowErrorScreen,
  } = props;

  const { tenantInfo }: any = useSelector((state: RootState) => state.tenant);
  const qrRef = useRef<HTMLCanvasElement>(null);
  const [documentData, setDocumentData] = useState(null as any);

  useEffect(() => {
    let businessId = tenantInfo?.tenantLogoDocumentDetail?.businessDocumentId;
    getDocUpload(businessId);
  }, [qRCodeType === "Connector"]);

  const handlePrintingInstructions = () => {
    setPrintingInstructions(true);
    setShowQRCode(false);
  };

  const getDocUpload = (Id: any) => {
    API_SERVICE.getDocDetails(Id)
      .then(({ data }) => {
        setDocumentData(data?.payload?.documentDetails);
      })
      .catch((e: any) => {
        API_SERVICE.handleErrors(e);
      });
  };

  const downloadQRCode = (
    qrCanvas: HTMLCanvasElement | null,
    userName: string,
    type: string,
    fileName = "Qrcode.png"
  ) => {
    if (qrCanvas) {
      const dpi = 96; // Convert cm to pixels
      const canvasWidth = 4 * dpi; // 4cm width (~151px)
      const canvasHeight = 6 * dpi; // 6cm height (~227px)
      const textHeight = 40; // Space for username
      const qrSize = canvasWidth * 0.8; // QR Code is 85% of width
      const centerWhiteCircleSize = qrSize * 0.25; // White cutout in the center

      // Create a new canvas for final image
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = canvasWidth;
      finalCanvas.height = canvasHeight;
      const ctx = finalCanvas.getContext("2d");
      if (ctx) {
        // **Step 1: Set White Background**
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        if (qRCodeType === "Connector") {
          const tenantLogo = new Image();
          tenantLogo.src = `data:image/png;base64,${documentData}`; // Update with correct path

          tenantLogo.onload = () => {
            ctx.font = "10px Arial";
            ctx.fillStyle = "black";
            const tenantLogoWidth = 200;
            const tenantLogoHeight = 50;
            ctx.drawImage(
              tenantLogo,
              canvasWidth / 4,
              canvasHeight / 10,
              tenantLogoWidth,
              tenantLogoHeight
            );
          };
          tenantLogo.onerror = () =>
            console.error("Error loading Tenant logo.");

          const lineStartX = canvasWidth * 0.1; // Start 10% from left
          const lineEndX = canvasWidth * 0.9; // End 10% before right
          const lineY = canvasHeight / 4.85; // Slightly below the logo

          ctx.beginPath();
          ctx.moveTo(lineStartX, lineY);
          ctx.lineTo(lineEndX, lineY);
          ctx.strokeStyle = "grey"; // Line color
          ctx.lineWidth = 0.5; // Line thickness
          ctx.stroke();
        }
        // **Step 2: Draw Username at the Top**
        ctx.font = "bold 20px Arial";
        ctx.textAlign = "center";
        ctx.fillStyle = "black";
        ctx.fillText(userName, canvasWidth / 2, canvasHeight / 4);

        // // **Step 3: Draw the QR Code**
        const qrX = (canvasWidth - qrSize) / 2;
        const qrY = textHeight * 4;
        ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

        // // **Step 4: Add White Circle in the Center of QR Code**
        // ctx.fillStyle = "white";
        // ctx.beginPath();
        // ctx.arc(canvasWidth / 2, // Center X
        //         qrY + qrSize / 2, // Center Y
        //         centerWhiteCircleSize / 2, // Radius
        //         0,
        //         Math.PI * 2);
        // ctx.fill();

        if (qRCodeType === "Connector") {
          ctx.font = "bold 14px Arial";
          ctx.textAlign = "center";
          ctx.fillStyle = "black";
          ctx.fillText("Scan To Sign Up", canvasWidth / 2, canvasHeight - 80);

          ctx.font = "bold 14px Arial";
          ctx.textAlign = "center";
          ctx.fillStyle = "black";
          ctx.fillText(
            tenantInfo?.tenantShortName ?? "",
            canvasWidth / 2,
            canvasHeight - 55
          );
        }
        // **Step 5: Draw "Powered by 1Flo" Text & Logo**
        const poweredByText = "Powered by ";
        const floLogo = new Image();
        floLogo.src = "/oneflo-final-logo 2.png"; // Update with correct path
        floLogo.crossOrigin = "anonymous";

        floLogo.onload = async () => {
          ctx.font = "12px Arial";
          ctx.fillStyle = "black";

          const textWidth = ctx.measureText(poweredByText).width;
          const floLogoWidth = 60;
          const floLogoHeight = 20;
          const totalWidth = textWidth + floLogoWidth + 10;
          const startX = (canvasWidth - totalWidth) / 2;
          const baselineY = qrY + qrSize + 50;

          ctx.fillText(poweredByText, startX + 30, baselineY + 45);
          ctx.drawImage(
            floLogo,
            textWidth + 140,
            baselineY - floLogoHeight + 45,
            floLogoWidth,
            floLogoHeight
          );

          const qrImageUrl = finalCanvas.toDataURL("image/png");
          if (type === "share") {
            try {
              // Convert Canvas to Blob
              const blob: Blob = await new Promise((resolve, reject) => {
                finalCanvas.toBlob((b) => {
                  if (b) resolve(b);
                  else reject(new Error("Failed to create image blob"));
                }, "image/png");
              });
              const file = new File([blob], fileName, { type: "image/png" });
              if (!navigator.canShare({ files: [file] })) {
                console.error("Sharing files is not supported on this device");
                return;
              }
              await navigator.share({
                title: "QR Code",
                text: `Check out this QR Code! ${qRCodeUrl}`,
                files: [file],
              });
            } catch (error) {
              console.error("Error sharing QR Code:", error);
            }
          } else {
            const link = document.createElement("a");
            link.href = qrImageUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        };
        floLogo.onerror = () => console.error("Error loading 1Flo logo.");
      }
    }
  };

  const renderQRCode = () => {
    return (
      <div className="qrcode-main-div">
        <div className="qrcode">
          <div className="qrcode-header">
            <Button
              onClick={() => {
                setShowQRCode(false);
                setPrintingInstructions(false);
                setShowErrorScreen(false);
              }}
            >
              <IconBackArrow />
              <span>Back</span>
            </Button>
            {!showErrorScreen && (
              <Button
                onClick={() =>
                  downloadQRCode(
                    qrRef.current,
                    getUser()?.userDetails?.fullName,
                    "download"
                  )
                }
              >
                <IconDownload />
                <span>Download</span>
              </Button>
            )}
          </div>
          <div className="qrcode-body">
            {showErrorScreen ? (
              <div className="qrcode-error">{errorMessage}</div>
            ) : (
              <>
                <div className="qrcode-body-name">
                  <div className="qr-name-firstletter">
                    {getUser()?.userDetails?.fullName?.at(0) ?? " "}
                  </div>
                  {getUser()?.userDetails?.fullName}
                </div>

                {!showErrorScreen && (
                  <div className="">
                    <QRCodeCanvas
                      value={qRCodeUrl}
                      size={250}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      ref={qrRef}
                    />
                  </div>
                )}

                {!showErrorScreen && qRCodeType === "Connector" ? (
                  <>
                    <div>Scan to Sign Up</div>
                    <div>{tenantInfo?.tenantShortName}</div>
                  </>
                ) : (
                  <div style={{ height: "20px" }} />
                )}
              </>
            )}
          </div>
          {!showErrorScreen && (
            <div className="qrcode-footer-buttons">
              <Button
                onClick={() => handlePrintingInstructions()}
                className="btn-print"
              >
                <IconPrint />
                Printing Instructions
              </Button>
              <Button
                className="btn-share-qr-code"
                type="link"
                onClick={(e) => {
                  downloadQRCode(
                    qrRef.current,
                    getUser()?.userDetails?.fullName,
                    "share"
                  );
                }}
              >
                <ShareAltOutlined
                  style={{ fontSize: 20, stroke: "white", strokeWidth: 5 }}
                />
                <div> Share QR Code </div>
              </Button>
            </div>
          )}
          <div className="qrcode-footer">
            <Text>Powered By</Text>
            <Icon1Flo />
          </div>
        </div>
      </div>
    );
  };

  const renderPrintingInstructions = () => {
    return (
      <div className="instructions-main-div">
        <div className="instructions-view">
          <div className="inst-header">
            <Button
              onClick={() => {
                setShowQRCode(true);
                setPrintingInstructions(false);
              }}
            >
              <IconBackArrow />
              <span>Back</span>
            </Button>
            <div> Printing Instructions</div>
            <div />
          </div>
          <div className="inst-body">
            <div className="inst-header">1. Use the Provided PNG File:</div>
            <div className="inst-text">
              1.1 Ensure the QR code is printed from the original
              high-resolution PNG file.
            </div>
            <div className="inst-text">
              1.2 Do not alter, crop, or stretch the image—maintain its original
              dimensions.
            </div>
            <div className="inst-header">2. Print Specifications:</div>
            <div className="inst-text">
              2.1 Recommended print area size: 6” x 4” for best readability.
            </div>
            <div className="inst-text">
              2.2 Maintain high contrast (black QR on a white/light background).
            </div>
            <div className="inst-text">
              2.3 Use non-glossy paper or a matte finish to prevent scanning
              issues.
            </div>
            <div className="inst-header">3. Preserve QR Code Integrity:</div>
            <div className="inst-text">
              3.1 Maintain whitespace (quiet zone) around the QR code for
              accurate scanning.
            </div>
            <div className="inst-text">
              3.2 Ensure the QR code remains unaltered throughout the printing
              process.
            </div>
            <div className="inst-header">4. Verification Before Use:</div>
            <div className="inst-text">
              4.1 Perform a test scan after printing to confirm redirection and
              functionality.
            </div>
            <div className="inst-text">
              4.2 If scanning fails, re-download and reprint with proper
              settings.
            </div>
            <div className="inst-last" style={{ marginTop: "20px" }}>
              For best results, follow these guidelines to ensure seamless
              scanning and usability.
            </div>
          </div>
          <div className="inst-footer">
            <Text>Powered By</Text>
            <Icon1Flo />
          </div>
        </div>
      </div>
    );
  };

  return <>{showQRCode ? renderQRCode() : renderPrintingInstructions()}</>;
};

export default QRCodeGenerator;
